import axios from 'axios';
import ApiUrl from './config';
import { ApproveBracketRequest, BracketPreviewModel, RoundApproveDto, MatchApproveDto, MixedGroupAssignmentDto } from './apiClient';

export enum CompetitionFormat {
  Olympic = 'Olympic',
  RoundRobin = 'RoundRobin',
  Mixed = 'Mixed',
}

export interface CategoryParticipantModel {
  id: number;
  personName: string;
  organization: string;
  weight: number;
  rank: string;
}

export interface MixedGroupModel {
  groupIndex: number;
  expectedSize: number;
  participants: CategoryParticipantModel[];
}

export interface MixedGroupAssignment {
  participantId: number;
  groupIndex: number;
}

export interface RoundRobinStandingModel {
  participantId: number;
  personName: string;
  organization: string;
  wins: number;
  losses: number;
  points: number;
  ippons: number;
  place?: number | null;
}

export interface CategoryDrawModel {
  tournamentId: number;
  tournamentName: string;
  weightCategory: string;
  ageCategory: string;
  participantCount: number;
  format: string;
  autoDetectedFormat: string;
  isFormatOverridden: boolean;
  participants: CategoryParticipantModel[];
  standings: RoundRobinStandingModel[];
  groups: MixedGroupModel[];
  rounds: {
    round: number;
    matches: {
      homeParticipantId?: number | null;
      homeParticipantName?: string | null;
      awayParticipantId?: number | null;
      awayParticipantName?: string | null;
    }[];
  }[];
}

export const formatLabel = (format: string) => {
  if (format === CompetitionFormat.RoundRobin) return 'Круговая';
  if (format === CompetitionFormat.Mixed) return 'Смешанная';
  return 'Олимпийская';
};

export function buildMixedGroupAssignments(draw: CategoryDrawModel): MixedGroupAssignment[] {
  return draw.groups.flatMap(group =>
    group.participants.map(p => ({
      participantId: p.id,
      groupIndex: group.groupIndex,
    }))
  );
}

export async function fetchCategoryDraw(
  tournamentId: number,
  weightCategoryInTournamentId: number,
  ageCategoryInTournamentId: number,
  isFormatOverridden = false,
  manualFormat?: CompetitionFormat
): Promise<CategoryDrawModel> {
  const params = new URLSearchParams({
    tournamentId: String(tournamentId),
    weightCategoryInTournamentId: String(weightCategoryInTournamentId),
    ageCategoryInTournamentId: String(ageCategoryInTournamentId),
    isFormatOverridden: String(isFormatOverridden),
  });
  if (manualFormat) params.set('manualFormat', manualFormat);

  const resp = await axios.get<CategoryDrawModel>(
    `${ApiUrl}/api/Tournament/GetCategoryDraw?${params.toString()}`
  );
  return resp.data;
}

export async function fetchPreviewBracket(
  tournamentId: number,
  weightCategoryInTournamentId: number,
  ageCategoryInTournamentId: number,
  isFormatOverridden = false,
  manualFormat?: CompetitionFormat
): Promise<BracketPreviewModel> {
  const params = new URLSearchParams({
    tournamentId: String(tournamentId),
    weightCategoryInTournamentId: String(weightCategoryInTournamentId),
    ageCategoryInTournamentId: String(ageCategoryInTournamentId),
    isFormatOverridden: String(isFormatOverridden),
  });
  if (manualFormat) params.set('manualFormat', manualFormat);

  const resp = await axios.get<BracketPreviewModel>(
    `${ApiUrl}/api/Tournament/PreviewBracket?${params.toString()}`
  );
  return resp.data;
}

export function buildApproveRequest(
  draw: CategoryDrawModel,
  ageCategoryInTournamentId: number,
  weightCategoryInTournamentId: number,
  isFormatOverridden: boolean,
  format: string
): ApproveBracketRequest {
  return new ApproveBracketRequest({
    tournamentId: draw.tournamentId,
    ageCategoryInTournamentId,
    weightCategoryInTournamentId,
    format,
    isFormatOverridden,
    rounds: draw.rounds.map(r => new RoundApproveDto({
      roundNumber: r.round,
      matches: r.matches.map(m => new MatchApproveDto({
        homeParticipantId: m.homeParticipantId ?? undefined,
        awayParticipantId: m.awayParticipantId ?? undefined,
      })),
    })),
    groupAssignments: buildMixedGroupAssignments(draw).map(a => new MixedGroupAssignmentDto({
      participantId: a.participantId,
      groupIndex: a.groupIndex,
    })),
  });
}

export function moveParticipantToGroup(
  draw: CategoryDrawModel,
  participantId: number,
  targetGroupIndex: 1 | 2
): CategoryDrawModel {
  const normalizedId = Number(participantId);
  const participant = draw.participants.find(p => Number(p.id) === normalizedId);
  const targetExists = draw.groups.some(g => Number(g.groupIndex) === targetGroupIndex);

  if (!targetExists || !participant) return draw;

  const groups = draw.groups.map(g => ({
    ...g,
    participants:
      Number(g.groupIndex) === targetGroupIndex
        ? [...g.participants.filter(p => Number(p.id) !== normalizedId), participant]
        : g.participants.filter(p => Number(p.id) !== normalizedId),
  }));

  return { ...draw, groups };
}

export function getExpectedGroupSizes(count: number): [number, number] {
  if (count === 6) return [3, 3];
  if (count === 7) return [3, 4];
  if (count === 8) return [4, 4];
  return [0, 0];
}

/** Допустимо распределение size1+size2 и size2+size1 (для 7 участников — 3+4 или 4+3). */
export function isValidMixedGroupDistribution(
  participantCount: number,
  group1Size: number,
  group2Size: number
): boolean {
  if (group1Size + group2Size !== participantCount) return false;
  const [expected1, expected2] = getExpectedGroupSizes(participantCount);
  if (expected1 === 0) return false;
  return (group1Size === expected1 && group2Size === expected2)
    || (group1Size === expected2 && group2Size === expected1);
}

export function isMixedEligible(count: number) {
  return count >= 6 && count <= 8;
}
